// src/components/PlotAvailability.jsx
import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import Map, { Source, Layer, Popup, NavigationControl, FullscreenControl, GeolocateControl } from 'react-map-gl/mapbox';
import { MAPBOX_CONFIG } from '../config/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import eventBus, { EVENTS } from '../utils/eventBus';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Status color configuration (3D)
const statusConfig = {
  available: {
    color: '#22c55e',
    label: 'Available',
    height: 0.254
  },
  unavailable: {
    color: '#f59e0b',
    label: 'Unavailable',
    height: 0.254
  },
  reserved: {
    color: '#3b82f6',
    label: 'Reserved',
    height: 0.254
  },
  occupied: {
    color: '#ef4444',
    label: 'Occupied',
    height: 0.254
  }
};

const getStatusColor = (status) => {
  return statusConfig[status]?.color || statusConfig.available.color;
};

const getStatusLabel = (status) => {
  return statusConfig[status]?.label || 'Available';
};

const getStatusHeight = (status) => {
  return statusConfig[status]?.height || 0.254;
};

export default function PlotAvailability() {
  const { token, role } = useContext(AuthContext);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  // State
  const [viewport, setViewport] = useState({
    latitude: MAPBOX_CONFIG.defaultViewport.latitude,
    longitude: MAPBOX_CONFIG.defaultViewport.longitude,
    zoom: MAPBOX_CONFIG.defaultViewport.zoom,
    bearing: 0,
    pitch: 60 // 3D view
  });
  
  const [gardenAData, setGardenAData] = useState(null);
  const [gardenBData, setGardenBData] = useState(null);
  const [gardenCData, setGardenCData] = useState(null);
  const [gardenDData, setGardenDData] = useState(null);
  const [allGraves, setAllGraves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('available');
  const [currentMapStyle] = useState(MAPBOX_CONFIG.defaultMapStyle);
  
  // Dynamically import CSS based on role
  useEffect(() => {
    if (role === 'client') {
      import('./PlotAvailabilityClient.css');
    } else {
      import('./PlotAvailability.css');
    }
  }, [role]);

  // No longer using fake status - will use real status from database
  // This function is kept for backward compatibility but is no longer used
  const getRealisticStatus = useCallback((name, row, column) => {
    // Deprecated - real status is now fetched from database
    return 'available';
  }, []);

  // Load GeoJSON data for gardens
  useEffect(() => {
    const loadGardenData = async () => {
    try {
      setLoading(true);
        
        // Load all garden GeoJSON files from backend API (with real database status)
        const API_URL = process.env.REACT_APP_API_URL || '';
        const [gardenAResponse, gardenBResponse, gardenCResponse, gardenDResponse] = await Promise.all([
          fetch(`${API_URL}/public/garden-a`).then(r => r.json()),
          fetch(`${API_URL}/public/garden-b`).then(r => r.json()),
          fetch(`${API_URL}/public/garden-c`).then(r => r.json()),
          fetch(`${API_URL}/public/garden-d`).then(r => r.json())
        ]);
        
        setGardenAData(gardenAResponse);
        setGardenBData(gardenBResponse);
        setGardenCData(gardenCResponse);
        setGardenDData(gardenDResponse);
        
        // Extract all graves with real status from the API
        const graves = [];
        [
          { data: gardenAResponse, name: 'A' },
          { data: gardenBResponse, name: 'B' },
          { data: gardenCResponse, name: 'C' },
          { data: gardenDResponse, name: 'D' }
        ].forEach(({ data, name }) => {
          if (data?.features) {
            data.features.forEach(feature => {
              if (feature.properties?.type === 'grave') {
                const props = feature.properties;
                const row = props.row || props.grave_row;
                const column = props.column || props.grave_column;
                // Use real status from API (already included in the GeoJSON from backend)
                const status = props.status || 'available';
                
                // Calculate centroid of the polygon for popup position
                let centerLng = 0, centerLat = 0;
                if (feature.geometry && feature.geometry.coordinates && feature.geometry.coordinates[0]) {
                  const coords = feature.geometry.coordinates[0]; // Get outer ring
                  coords.forEach(([lng, lat]) => {
                    centerLng += lng;
                    centerLat += lat;
                  });
                  centerLng /= coords.length;
                  centerLat /= coords.length;
                }
                
                graves.push({
                  id: props.name || `${name}-${row}-${column}`,
                  garden: name,
                  row,
                  column,
                  status,
                  coordinates: [centerLng, centerLat], // Store as [lng, lat] point
                  location: `Garden ${name}, Row ${row}, Column ${column}`,
                  sqm: '2',
                  price: 8000,
                  feature
                });
              }
            });
          }
        });
        
        setAllGraves(graves);
      setLoading(false);
    } catch (err) {
        console.error('Error loading garden data:', err);
        setError('Could not load cemetery data. Please try again later.');
      setLoading(false);
    }
  };

    loadGardenData();

    // Subscribe to lot and reservation events
    const handleUpdate = () => loadGardenData();
    eventBus.on(EVENTS.LOT_UPDATED, handleUpdate);
    eventBus.on(EVENTS.LOT_CREATED, handleUpdate);
    eventBus.on(EVENTS.LOT_DELETED, handleUpdate);
    eventBus.on(EVENTS.RESERVATION_CHANGED, handleUpdate);

    return () => {
      eventBus.off(EVENTS.LOT_UPDATED, handleUpdate);
      eventBus.off(EVENTS.LOT_CREATED, handleUpdate);
      eventBus.off(EVENTS.LOT_DELETED, handleUpdate);
      eventBus.off(EVENTS.RESERVATION_CHANGED, handleUpdate);
    };
  }, [getRealisticStatus]);

  // Handle map style load
  const handleMapLoad = useCallback(() => {
    setIsStyleLoaded(true);
  }, []);

  // Handle grave click
  const handleGraveClick = useCallback((event) => {
    if (!event.features || event.features.length === 0) return;
    
    const feature = event.features[0];
    
    // Check if it's a grave layer (not niche)
    if (feature.layer && feature.layer.id && feature.layer.id.includes('graves')) {
      const props = feature.properties;
      const row = props.row || props.grave_row;
      const column = props.column || props.grave_column;
      const gardenLetter = props.garden;
      
      // Find the grave in our data
      const grave = allGraves.find(g => 
        g.row === row && g.column === column && g.garden === gardenLetter
      );
      
      if (grave && grave.status === 'available' && grave.coordinates) {
        // Use grave coordinates from our data (they're in [lng, lat] format)
        const [lng, lat] = grave.coordinates;
        
        setSelectedLot(grave);
        setPopupInfo({
          longitude: lng,
          latitude: lat,
          grave
        });
      }
    }
  }, [allGraves]);

  // Add price calculation helper
  const calculatePrice = (sqm, pricePerSqm = 4000) => {
    const sqmValue = parseFloat(sqm) || 0;
    return sqmValue * pricePerSqm;
  };

  // Render 3D graves for a garden
  const renderGarden3D = (gardenData, gardenName) => {
    if (!gardenData || !isStyleLoaded) {
      return null;
    }

    const allGraves = (gardenData?.features || []).filter(
      feature => feature && feature.properties && feature.properties.type === 'grave'
    );
    
    const allNiches = (gardenData?.features || []).filter(
      feature => feature && feature.properties && feature.properties.type === 'niche'
    );

    // Render by status
    if (statusFilter === 'all') {
      return (
        <>
          {['available', 'occupied', 'reserved', 'unavailable'].map(status => {
            const graves = allGraves.filter(feature => {
              const props = feature.properties;
              // Use real status from properties (already set from database)
              return (props.status || 'available') === status;
            });

            if (graves.length === 0) return null;

            const gravesWithGarden = graves.map(g => ({
              ...g,
              properties: {
                ...g.properties,
                garden: gardenName
              }
            }));

            return (
              <Source
                key={`garden-${gardenName}-graves-${status}`}
                id={`garden-${gardenName}-graves-${status}`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: gravesWithGarden
                }}
              >
                <Layer
                  id={`garden-${gardenName}-graves-fill-${status}`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': getStatusColor(status),
                    'fill-extrusion-height': getStatusHeight(status),
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })}

          {/* Render niches only for available graves */}
          {(() => {
            const availableGraves = allGraves.filter(feature => {
              const props = feature.properties;
              // Use real status from properties
              return (props.status || 'available') === 'available';
            });

            const availableLocations = new Set();
            availableGraves.forEach(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              availableLocations.add(`${row}-${column}`);
            });

            const availableNiches = allNiches.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return availableLocations.has(`${row}-${column}`);
            });

            if (availableNiches.length === 0) return null;

            return (
              <Source
                key={`garden-${gardenName}-niches`}
                id={`garden-${gardenName}-niches`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: availableNiches
                }}
              >
                <Layer
                  id={`garden-${gardenName}-niches-fill`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': '#6b7280',
                    'fill-extrusion-height': 0.381,
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })()}
        </>
      );
    } else {
      const graves = allGraves.filter(feature => {
        const props = feature.properties;
        // Use real status from properties
        return (props.status || 'available') === statusFilter;
      });

      if (graves.length === 0) return null;

      const gravesWithGarden = graves.map(g => ({
        ...g,
        properties: {
          ...g.properties,
          garden: gardenName
        }
      }));

      return (
        <>
          <Source
            key={`garden-${gardenName}-graves-filtered`}
            id={`garden-${gardenName}-graves-filtered`}
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: gravesWithGarden
            }}
          >
            <Layer
              id={`garden-${gardenName}-graves-fill-filtered`}
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': getStatusColor(statusFilter),
                'fill-extrusion-height': getStatusHeight(statusFilter),
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': 1.0
              }}
            />
          </Source>

          {/* Render niches in filtered view */}
          {(() => {
            const filteredLocations = new Set();
            graves.forEach(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              filteredLocations.add(`${row}-${column}`);
            });

            const filteredNiches = allNiches.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return filteredLocations.has(`${row}-${column}`);
            });

            if (filteredNiches.length === 0) return null;

            return (
              <Source
                key={`garden-${gardenName}-niches-filtered`}
                id={`garden-${gardenName}-niches-filtered`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: filteredNiches
                }}
              >
                <Layer
                  id={`garden-${gardenName}-niches-fill-filtered`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': '#6b7280',
                    'fill-extrusion-height': 0.381,
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })()}
        </>
      );
    }
  };

  if (loading) {
    return (
      <div className="plot-availability-wrapper">
        <div className="loading-spinner">Loading 3D cemetery map...</div>
      </div>
    );
  }

  return (
    <div className="plot-availability-wrapper">
      {error && <div className="error-banner">{error}</div>}

      {/* Mapbox 3D Map */}
      <Map
        ref={mapRef}
        {...viewport}
        onMove={evt => setViewport(evt.viewState)}
        mapboxAccessToken={MAPBOX_CONFIG.accessToken}
        style={{ width: '100%', height: '100%' }}
        mapStyle={currentMapStyle}
        onLoad={handleMapLoad}
        onClick={handleGraveClick}
        interactiveLayerIds={
          statusFilter === 'all'
            ? [
                'garden-A-graves-fill-available',
                'garden-B-graves-fill-available',
                'garden-C-graves-fill-available',
                'garden-D-graves-fill-available'
              ]
            : statusFilter === 'available'
            ? [
                'garden-A-graves-fill-filtered',
                'garden-B-graves-fill-filtered',
                'garden-C-graves-fill-filtered',
                'garden-D-graves-fill-filtered'
              ]
            : []
        }
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />
        <GeolocateControl position="top-right" />

        {renderGarden3D(gardenAData, 'A')}
        {renderGarden3D(gardenBData, 'B')}
        {renderGarden3D(gardenCData, 'C')}
        {renderGarden3D(gardenDData, 'D')}

        {popupInfo && selectedLot && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            anchor="bottom"
            onClose={() => {
              setPopupInfo(null);
              setSelectedLot(null);
            }}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="lot-popup">
              <h4>Available Plot</h4>
              <p><strong>Plot ID:</strong> {selectedLot.id}</p>
              <p><strong>Location:</strong> {selectedLot.location}</p>
              <p><strong>Size:</strong> {selectedLot.sqm} sqm</p>
              <p><strong>Price:</strong> ₱{calculatePrice(selectedLot.sqm).toLocaleString()}</p>
              <p><strong>Status:</strong> <span className="status-badge available">
                {getStatusLabel(selectedLot.status)}
              </span></p>
              
                <button
                className="reserve-btn"
                  onClick={() => {
                    const lotData = {
                      id: selectedLot.id,
                    name: selectedLot.name || '',
                      sqm: selectedLot.sqm,
                      location: selectedLot.location,
                      status: selectedLot.status,
                    price: selectedLot.price || calculatePrice(selectedLot.sqm),
                      // Add grave-specific data for new 3D system
                      garden: selectedLot.garden,
                      row: selectedLot.row,
                      column: selectedLot.column
                    };
                    
                    // Navigate to appropriate booking route based on user role
                  let basePath = '/staff';
                    if (role === 'client') {
                    basePath = '/client';
                  } else if (role === 'admin') {
                    basePath = '/admin';
                  }
                  
                  // Navigate to the booking (Reserve) tab
                  navigate(`${basePath}/reservations/booking`, { 
                        state: { 
                          lotDetails: lotData,
                      isStaffBooking: role === 'staff' || role === 'admin',
                      isClientBooking: role === 'client'
                        } 
                      });
                  setPopupInfo(null);
                    setSelectedLot(null);
                  }}
                >
                  RESERVE
                </button>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
}